import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter } from 'react-router-dom'
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material'

import { initDatadogLogs, initDatadogRUM } from '@/lib/datadog'
import { initGoogleMapsAPI } from '@/lib/googleMaps'
import { validateEnv } from '@/lib/urlBuilder'
import { ldClient } from '@/lib/launchDarkly'
import { queryClient } from '@/lib/queryClient'
import { store } from '@/store'
import { theme } from '@/styles/theme'
import { AuthProvider } from '@/providers/AuthProvider'
import { FeatureFlagProvider } from '@/providers/FeatureFlagProvider'
import { I18nProvider } from '@/providers/I18nProvider'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import App from './App'

import './styles/global.scss'

validateEnv()

initDatadogLogs()

initDatadogRUM()

void initGoogleMapsAPI()

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element not found')

ReactDOM.createRoot(rootElement).render(
	<React.StrictMode>
		{/*
		 * Provider nesting order:
		 * Provider (Redux) → QueryClientProvider → ThemeProvider → BrowserRouter → ErrorBoundary
		 *   → AuthProvider → FeatureFlagProvider → I18nProvider
		 *
		 * Provider (Redux) is outermost so the auth store is available to all providers,
		 * including AuthProvider which reads/writes auth state via useAppSelector/useAppDispatch.
		 * QueryClientProvider is next so every provider below can call useQuery if needed.
		 * ThemeProvider wraps everything so MUI components anywhere in the tree get brand tokens.
		 * BrowserRouter is before AuthProvider so auth can read the current route.
		 * ErrorBoundary wraps all meaningful providers so render errors surface the fallback UI.
		 * AuthProvider is before FeatureFlagProvider so LD user context can be set from auth state.
		 * FeatureFlagProvider is before I18nProvider so language can be driven by a feature flag later.
		 */}
		<Provider store={store}>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					{/* overflow-x clip on html/body would create a new scroll container and
					    break position:sticky. Use clip instead — it prevents the horizontal
					    scrollbar without establishing a scroll container. */}
					<GlobalStyles
						styles={{
							html: { scrollBehavior: 'smooth', overflowX: 'clip' },
							body: { overflowX: 'clip' },
						}}
					/>
					<BrowserRouter>
						<ErrorBoundary fullPage>
							<AuthProvider>
								<FeatureFlagProvider ldClient={ldClient}>
									<I18nProvider>
										<App />
									</I18nProvider>
								</FeatureFlagProvider>
							</AuthProvider>
						</ErrorBoundary>
					</BrowserRouter>
				</ThemeProvider>
				{import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
			</QueryClientProvider>
		</Provider>
	</React.StrictMode>,
)
